import React, { FC, useState } from 'react';

import { EditTwoTone } from '@ant-design/icons';
import { theme as antTheme, Table } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { NSKeys } from '@app/i18n';
import { RetrieveUserSchema } from '@app/models/users';
import { useWindowSize } from '@app/services/hooks/useWindowSize';
import { TableEditMenu } from '@components/TableEditMenu';
import { useGetEmployeesQuery } from '@pages/Employees/hooks';

import { ACTIVE_FILTER_VALUE, DISABLED_FILTER_VALUE, Filters } from './components/Filters';
import { SelectedRowActions } from './components/SelectedRowActions';
import { baseColumns } from './data/constants';
import { Container } from './styles';

interface IProps {
  setPagination: (newCurrent: number, newPageSize: number) => void;
  current: number;
  pageSize: number;
  nameContains?: string;
}

export const TableList: FC<IProps> = ({ current, pageSize, setPagination, nameContains }) => {
  const navigate = useNavigate();
  const [columns, setColumns] = useState<ColumnsType<RetrieveUserSchema>>(baseColumns);
  const [isActive, setIsActive] = useState<boolean | undefined>();
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  const commonT = useTranslation(NSKeys.common);
  const employeesT = useTranslation(NSKeys.employees);
  const { innerHeight } = useWindowSize();
  const { token } = antTheme.useToken();
  const { data, isLoading } = useGetEmployeesQuery({
    offset: (current - 1) * pageSize,
    limit: pageSize,
    nameContains,
    isActive,
  });

  const handleTableHeaderChange = (checkedValues: any[]) => {
    setColumns(baseColumns.filter((item) => checkedValues.includes(item.key as string)));
  };

  const handleFiltersChange = (query: string) => {
    const nextIsActive = query.includes(`is_active=="${ACTIVE_FILTER_VALUE}"`)
      ? true
      : query.includes(`is_active=="${DISABLED_FILTER_VALUE}"`)
      ? false
      : undefined;

    setIsActive(nextIsActive);
    setPagination(1, pageSize);
  };

  return (
    <Container>
      <Filters onChange={handleFiltersChange} />
      {selectedRowKeys.length > 0 && (
        <SelectedRowActions selectedRows={selectedRowKeys} onRowsReset={() => setSelectedRowKeys([])} />
      )}
      <Table
        loading={isLoading}
        dataSource={data?.items}
        rowKey="pk"
        rowSelection={{
          selectedRowKeys,
          onChange: (keys) => setSelectedRowKeys(keys),
          getCheckboxProps: (record) => ({ disabled: record.$permissions.update !== true }),
        }}
        className="tableClassname"
        columns={[
          ...columns.map((el) => ({ ...el, title: employeesT.t(el.title as any) })),
          {
            title: (
              <TableEditMenu
                defaultValue={baseColumns.map((el) => el.key as string)}
                handleOnChange={handleTableHeaderChange}
                options={baseColumns.map((el) => ({
                  value: el.key as string,
                  label: employeesT.t(el.title || (el.key as any)),
                }))}
              />
            ),
            fixed: 'right',
            key: 'action',
            align: 'center',
            width: 40,
            render: (_, cell) =>
              cell.$permissions.update && (
                <EditTwoTone
                  twoToneColor={token.colorPrimary}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`edit/${cell.pk}`);
                  }}
                />
              ),
          },
        ]}
        pagination={{
          current: current,
          pageSize: pageSize,
          total: data?.total,
          showQuickJumper: true,
          showSizeChanger: true,
          position: ['bottomCenter'],
          showTotal: (total, range) => `${range[0]}-${range[1]} ${commonT.t('paginationOf')} ${total}`,
          onChange: (newPage, newSize) => {
            setPagination(newPage, newSize);
          },
        }}
        rowClassName="cursorPointer"
        onRow={(data) => {
          return {
            onClick: () => navigate(`${data.pk}`),
          };
        }}
        scroll={{ y: innerHeight - 380, x: true }}
        style={{ overflow: 'auto' }}
      />
    </Container>
  );
};
